import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronRight, Search } from 'lucide-react';
import { CarteNote } from './CarteNote';
import { Chargement, MessageErreur, Pastille } from '../ui/Primitives';
import { ErreurApi } from '../../lib/api';
import { useValeurDifferee } from '../../lib/useRecherche';
import { adresseLecon, useMesNotes } from '../../lib/useLecteur';

/**
 * Espace « Mes notes » du lecteur (TF-446) : toutes les notes personnelles de
 * l'apprenant, par cours puis par leçon dans l'ordre du programme, avec une
 * recherche dans leur texte et dans les titres. Une note ouvre sa leçon à son
 * moment. Les notes d'une leçon qui ne s'ouvre plus (cours retiré, fin de
 * formation) restent ici : il les relit, les modifie, les supprime.
 */
export function MesNotes(): JSX.Element {
  const navigate = useNavigate();
  const [saisie, setSaisie] = useState('');
  const recherche = useValeurDifferee(saisie.trim(), 300);
  const { data, isLoading, error } = useMesNotes(recherche, true);

  if (isLoading) return <Chargement libelle="Chargement de vos notes…" />;
  if (error || !data) {
    return <MessageErreur message={error instanceof ErreurApi ? error.message : 'Vos notes n’ont pas pu être chargées.'} />;
  }
  if (data.total === 0) {
    return (
      <div className="rounded-2xl bg-gray-50 p-4 text-sm leading-relaxed text-codialis-gray sm:p-5">
        Vous n’avez pas encore de note. Ouvrez une leçon de vos cours en ligne : sous la vidéo, l’onglet « Mes notes »
        rattache chaque note au moment que vous regardez.
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
        <label className="relative min-w-0 flex-1" htmlFor="recherche-notes">
          <span className="sr-only">Rechercher dans mes notes</span>
          <Search
            className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-codialis-gray"
            aria-hidden="true"
          />
          <input
            id="recherche-notes"
            type="search"
            value={saisie}
            maxLength={200}
            onChange={(evenement) => setSaisie(evenement.target.value)}
            placeholder="Rechercher dans mes notes, une leçon, un cours…"
            className="w-full rounded-xl border border-gray-200 bg-white py-2 pl-10 pr-3.5 text-sm text-codialis-navy transition placeholder:text-gray-400 focus:border-codialis-sky focus:outline-none focus:ring-2 focus:ring-codialis-sky/20 [@media(pointer:coarse)]:min-h-[44px]"
          />
        </label>
        <span className="shrink-0 text-xs text-codialis-gray">
          {data.total} note{data.total > 1 ? 's' : ''} au total
        </span>
      </div>

      {data.cours.length === 0 ? (
        <p className="rounded-2xl bg-gray-50 p-4 text-sm text-codialis-gray">
          Aucune note ne correspond à « {recherche} ».
        </p>
      ) : (
        data.cours.map((cours) => (
          <section key={cours.id} aria-label={cours.titre} className="space-y-3">
            <h4 className="font-display font-bold text-codialis-navy">{cours.titre}</h4>
            {cours.lecons.map((lecon) => (
              <div key={lecon.id} className="rounded-2xl border border-gray-100 p-3 sm:p-4">
                <div className="mb-3 flex flex-wrap items-center gap-2">
                  <div className="min-w-0 flex-1 basis-48">
                    <div className="truncate text-sm font-semibold text-codialis-navy">
                      <span className="font-mono text-xs text-codialis-gray">{lecon.numero}</span> {lecon.titre}
                    </div>
                    <div className="truncate text-xs text-codialis-gray">{lecon.module}</div>
                  </div>
                  {lecon.lisible ? (
                    <button
                      type="button"
                      onClick={() => navigate(adresseLecon(lecon.id, 'notes'))}
                      className="inline-flex min-h-[36px] shrink-0 items-center gap-1 rounded-lg border border-gray-200 px-3 text-xs font-semibold text-codialis-navy hover:bg-gray-50 [@media(pointer:coarse)]:min-h-[44px]"
                    >
                      Ouvrir la leçon
                      <ChevronRight className="h-4 w-4" aria-hidden />
                    </button>
                  ) : (
                    <Pastille texte="Leçon fermée" ton="neutral" />
                  )}
                </div>
                {!lecon.lisible && (
                  <p className="mb-3 text-xs text-codialis-gray">
                    Cette leçon ne s’ouvre plus dans le lecteur : vos notes restent ici.
                  </p>
                )}
                <ol aria-label={`Mes notes sur ${lecon.titre}`} className="space-y-3">
                  {lecon.notes.map((note) => (
                    <CarteNote
                      key={note.id}
                      note={note}
                      allerA={
                        lecon.lisible ? () => navigate(adresseLecon(lecon.id, 'notes', note.momentSecondes)) : null
                      }
                    />
                  ))}
                </ol>
              </div>
            ))}
          </section>
        ))
      )}
    </div>
  );
}
